import React, { useState, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { Trash2 } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import {
  arrayMove,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  SortableContext,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDispatch, useSelector } from 'react-redux';
import type { Attachment } from '../../models/Attachment.interface';
import { updateJob } from '../../slices/jobs/reducer';
import type { RootState } from '../../index';
import CaptionEditor from './CaptionEditor';
import LoadingOverlay from '../../Components/LoadingOverlay';
import './FileUpload.css';

export interface FileUpdateResult {
  newFiles: File[];
  deletedUrls: string[];
  attachmentOrder: Attachment[];
  captions: Record<string, string>;
}

interface FileWrapper {
  id: string;
  type: string;
  name: string;
  size: number;
  isExisting: boolean;
  url: string;
  caption: string;
  file?: File;
}

interface FileUploadProps {
  onFilesChange: (files: File[]) => void;
  existingFiles?: Attachment[];
  onExistingFileDelete?: (url: string) => void;
  onAttachmentUpdate?: (result: FileUpdateResult) => void;
  jobId?: string;
}

interface SortableFileProps {
  fileWrapper: FileWrapper;
  onCaptionChange: (id: string, caption: string) => void;
  onDelete: (id: string) => void;
  jobId?: string;
  onSave: (id: string, caption: string) => Promise<void>;
}

// Image compression utility
const compressImage = (file: File, maxWidth: number = 1000, quality: number = 0.8): Promise<File> => {
  return new Promise((resolve) => {
    // If not an image, return original file
    if (!file.type.startsWith('image/')) {
      resolve(file);
      return;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Calculate new dimensions
      let { width, height } = img;

      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }

      canvas.width = width;
      canvas.height = height;

      // Draw and compress
      ctx?.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            // Create new file with compressed data
            const compressedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          } else {
            resolve(file);
          }
        },
        file.type,
        quality
      );
    };

    img.onerror = () => {
      // If image loading fails, return original file
      resolve(file);
    };

    img.src = URL.createObjectURL(file);
  });
};

// Batch compress multiple files
const compressFiles = async (files: File[], maxWidth: number = 1000, quality: number = 0.8): Promise<File[]> => {
  const compressionPromises = files.map(file => compressImage(file, maxWidth, quality));
  return Promise.all(compressionPromises);
};

const SortableFile: React.FC<SortableFileProps> = ({
  fileWrapper,
  onCaptionChange,
  onDelete,
  jobId,
  onSave
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: fileWrapper.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const renderPreview = () => {
    switch (fileWrapper.type) {
      case 'image':
        return (
          <img
            src={fileWrapper.url}
            alt="Preview"
            className="attachment-image"
            draggable={false}
          />
        );
      case 'video':
        return (
          <video controls className="attachment-image">
            <source src={fileWrapper.url} type="video/mp4" />
          </video>
        );
      case 'pdf':
        return (
          <object
            data={fileWrapper.url}
            type="application/pdf"
            className="attachment-image"
          >
            <div className="attachment-placeholder">
              <p>PDF Preview</p>
            </div>
          </object>
        );
      default:
        return (
          <div className="attachment-placeholder">
            <p>{fileWrapper.name}</p>
          </div>
        );
    }
  };

  return (
    <div ref={setNodeRef} style={style} className="attachment-item">
      <div {...attributes} {...listeners} className="drag-handle">⋮⋮</div>
      <button onClick={() => onDelete(fileWrapper.id)} className="delete-handle">
        <Trash2 className="w-4 h-4" />
      </button>
      <div className="attachment-preview">
        {renderPreview()}
        <CaptionEditor
          fileWrapper={fileWrapper}
          existingCaption={fileWrapper.caption}
          onSave={onSave}
          isNewUpload={!fileWrapper.isExisting}
        />
      </div>
    </div>
  );
};

const FileUpload: React.FC<FileUploadProps> = ({
  onFilesChange,
  existingFiles = [],
  onExistingFileDelete,
  onAttachmentUpdate,
  jobId
}) => {
  const dispatch = useDispatch();
  const currentJob = useSelector((state: RootState) =>
    jobId ? state.Jobs.jobs.find(job => job.id === jobId) : null
  );

  const [files, setFiles] = useState<FileWrapper[]>([]);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [localChanges, setLocalChanges] = useState<FileUpdateResult>({
    newFiles: [],
    deletedUrls: [],
    attachmentOrder: [],
    captions: {}
  });
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState<string>('');
  
  // Track the last processed existingFiles to prevent duplicate processing
  const lastExistingFilesRef = useRef<string>('');
  const processingFiles = useRef<Set<string>>(new Set());
  const mountedRef = useRef(true);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const getFileType = (url: string): string => {
    // For Firebase Storage URLs, extract the filename before the query parameters
    let filename = '';
    if (url.includes('firebasestorage.googleapis.com')) {
      // Extract the filename from the path component
      const matches = url.match(/\/([^/?]+)\?/);
      if (matches && matches[1]) {
        filename = matches[1];
      }
    } else {
      filename = url.split('/').pop() || '';
    }

    // Remove query parameters if present
    filename = filename.split('?')[0];

    // Get the extension
    const ext = filename.split('.').pop()?.toLowerCase() || '';

    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'image';
    if (['mp4', 'webm', 'mov'].includes(ext)) return 'video';
    if (ext === 'pdf') return 'pdf';
    return 'other';
  };

  const updateLocalChanges = (updatedFiles: FileWrapper[]) => {
    if (!mountedRef.current) return;

    const newChanges: FileUpdateResult = {
      newFiles: updatedFiles.filter(f => !f.isExisting && f.file).map(f => f.file!),
      deletedUrls: localChanges.deletedUrls,
      attachmentOrder: updatedFiles.map((file, index) => ({
        id: file.id,
        url: file.url,
        caption: file.caption,
        order: index
      })),
      captions: Object.fromEntries(
        updatedFiles.map(file => [file.id, file.caption])
      )
    };

    setLocalChanges(newChanges);
    if (onAttachmentUpdate) {
      onAttachmentUpdate(newChanges);
    }
  };

  const handleCaptionSave = async (id: string, caption: string) => {
    if (!mountedRef.current) return;

    const updatedFiles = files.map(file =>
      file.id === id ? { ...file, caption } : file
    );
    setFiles(updatedFiles);
    updateLocalChanges(updatedFiles);

    if (jobId && currentJob) {
      const updatedAttachments = updatedFiles.map((file, index) => ({
        id: file.id,
        url: file.url,
        caption: file.caption,
        order: index
      }));

      dispatch(updateJob({
        ...currentJob,
        jobAttachments: updatedAttachments
      }));
    }
  };

  const onDrop = async (acceptedFiles: File[]) => {
    if (!mountedRef.current) return;

    //console.log("🔵 [FileUpload] onDrop - Accepted files:", acceptedFiles.map(f => f.name));

    // Create a unique identifier for this batch of files to prevent duplicate processing
    const batchId = `batch-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    //console.log("🔵 [FileUpload] onDrop - Batch ID:", batchId);

    // Check if we're already processing files
    if (processingFiles.current.size > 0) {
      console.warn('🟡 [FileUpload] Already processing files, ignoring duplicate drop event');
      return;
    }

    // Filter out files that would create duplicates based on name, size, and lastModified
    const filteredFiles = acceptedFiles.filter(newFile => {
      const fileSignature = `${newFile.name}-${newFile.size}-${newFile.lastModified}`;

      // Check against existing files
      const isDuplicateExisting = files.some(existingFile =>
        existingFile.name === newFile.name &&
        existingFile.size === newFile.size
      );

      // Check against currently processing files
      const isDuplicateProcessing = processingFiles.current.has(fileSignature);

      if (isDuplicateExisting || isDuplicateProcessing) {
        console.warn(`🟡 [FileUpload] Skipping duplicate file: ${newFile.name}`);
        return false;
      }

      // Mark this file as being processed
      processingFiles.current.add(fileSignature);
      //console.log("🔵 [FileUpload] Processing file:", newFile.name);
      return true;
    });

    if (filteredFiles.length === 0) {
      //console.log("🟡 [FileUpload] No new files to add (all were duplicates)");
      return;
    }

    //console.log("🔵 [FileUpload] Filtered files for processing:", filteredFiles.map(f => f.name));

    // Show loading overlay for image compression
    const imageFiles = filteredFiles.filter(file => file.type.startsWith('image/'));
    if (imageFiles.length > 0) {
      setIsCompressing(true);
      setCompressionProgress(`Compressing ${imageFiles.length} image${imageFiles.length > 1 ? 's' : ''}...`);
      //console.log("🔵 [FileUpload] Starting compression for", imageFiles.length, "images");
    }

    try {
      // Compress images and keep other files as-is
      const compressedFiles = await compressFiles(filteredFiles, 1000, 0.8);

      if (!mountedRef.current) {
        // Component unmounted during compression
        //console.log("🟡 [FileUpload] Component unmounted during compression");
        return;
      }

      //console.log("🔵 [FileUpload] Compression complete. Compressed files:", compressedFiles.map(f => f.name));

      // Calculate compression savings for logging
      const originalSize = filteredFiles.reduce((sum, file) => sum + file.size, 0);
      const compressedSize = compressedFiles.reduce((sum, file) => sum + file.size, 0);
      const savings = originalSize > 0 ? ((originalSize - compressedSize) / originalSize * 100).toFixed(1) : '0';

      if (imageFiles.length > 0) {
        //console.log(`🔵 [FileUpload] Image compression complete. Size reduced by ${savings}%`);
        //console.log(`🔵 [FileUpload] Original: ${(originalSize / 1024 / 1024).toFixed(2)}MB, Compressed: ${(compressedSize / 1024 / 1024).toFixed(2)}MB`);
      }

      // Create wrappers for compressed files with unique IDs
      const newWrappers = compressedFiles.map((file, index) => ({
        id: `new-${batchId}-${index}`, // Ensure unique IDs
        file,
        type: file.type.startsWith('image/') ? 'image' :
          file.type.startsWith('video/') ? 'video' :
            file.type === 'application/pdf' ? 'pdf' : 'other',
        name: file.name,
        size: file.size,
        isExisting: false,
        url: URL.createObjectURL(file),
        caption: '',
      }));

      //console.log("🔵 [FileUpload] Created new wrappers:", newWrappers.map(w => ({ id: w.id, name: w.name })));

      // Update files state - only add new files, don't merge with existing
      const updatedFiles = [...files, ...newWrappers];
      //console.log("🔵 [FileUpload] Setting files state. Before:", files.length, "After:", updatedFiles.length);
      setFiles(updatedFiles);

      // Update newFiles separately to track actual File objects
      const updatedNewFiles = [...newFiles, ...compressedFiles];
      //console.log("🔵 [FileUpload] Setting newFiles state. Before:", newFiles.length, "After:", updatedNewFiles.length);
      setNewFiles(updatedNewFiles);

      // Call parent handler with just the File objects - this will trigger upload in CreateJobFormSteps
      //console.log("🔵 [FileUpload] Calling onFilesChange with:", updatedNewFiles.map(f => f.name));
      onFilesChange(updatedNewFiles);

    } catch (error) {
      console.error('🔴 [FileUpload] Error during file compression:', error);
      // Fallback to original files if compression fails
      if (mountedRef.current) {
        const newWrappers = filteredFiles.map((file, index) => ({
          id: `new-${batchId}-${index}`,
          file,
          type: file.type.startsWith('image/') ? 'image' :
            file.type.startsWith('video/') ? 'video' :
              file.type === 'application/pdf' ? 'pdf' : 'other',
          name: file.name,
          size: file.size,
          isExisting: false,
          url: URL.createObjectURL(file),
          caption: '',
        }));

        const updatedFiles = [...files, ...newWrappers];
        setFiles(updatedFiles);

        const updatedNewFiles = [...newFiles, ...filteredFiles];
        setNewFiles(updatedNewFiles);

        // Call parent handler with just the File objects
        onFilesChange(updatedNewFiles);
        //console.log("🔵 [FileUpload] Fallback: Called onFilesChange with:", updatedNewFiles.map(f => f.name));
      }
    } finally {
      // Clear processing flags for this batch
      filteredFiles.forEach(file => {
        const fileSignature = `${file.name}-${file.size}-${file.lastModified}`;
        processingFiles.current.delete(fileSignature);
      });

      if (mountedRef.current) {
        setIsCompressing(false);
        setCompressionProgress('');
        //console.log("🔵 [FileUpload] onDrop complete, compression finished");
      }
    }
  };

  const handleDelete = (id: string) => {
    if (!mountedRef.current) return;

    const fileToDelete = files.find(f => f.id === id);
    if (!fileToDelete) return;

    const updatedFiles = files.filter(f => f.id !== id);
    setFiles(updatedFiles);

    if (fileToDelete.isExisting && onExistingFileDelete) {
      onExistingFileDelete(fileToDelete.url);
      setLocalChanges(prev => ({
        ...prev,
        deletedUrls: [...prev.deletedUrls, fileToDelete.url]
      }));
    } else if (!fileToDelete.isExisting) {
      // Remove from newFiles as well
      const updatedNewFiles = newFiles.filter(file => {
        // Compare by name and size since blob URLs change
        return !(file.name === fileToDelete.name && file.size === fileToDelete.size);
      });
      setNewFiles(updatedNewFiles);
      onFilesChange(updatedNewFiles);

      // Clean up object URL
      URL.revokeObjectURL(fileToDelete.url);
    }

    updateLocalChanges(updatedFiles);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (!mountedRef.current) return;

    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = files.findIndex(item => item.id === active.id);
      const newIndex = files.findIndex(item => item.id === over.id);

      const updatedFiles = arrayMove(files, oldIndex, newIndex);
      setFiles(updatedFiles);

      // Update the order of newFiles to match the new arrangement
      const reorderedNewFiles: File[] = [];
      updatedFiles.forEach(fileWrapper => {
        if (!fileWrapper.isExisting && fileWrapper.file) {
          reorderedNewFiles.push(fileWrapper.file);
        }
      });

      setNewFiles(reorderedNewFiles);
      onFilesChange(reorderedNewFiles);
      updateLocalChanges(updatedFiles);
    }
  };

  // Initialize with existing files - FIXED to prevent duplicates
  useEffect(() => {
    if (!mountedRef.current || !existingFiles) return;

    // Create a signature of the existing files to detect actual changes
    const existingFilesSignature = existingFiles.map(f => f.url).sort().join('|');
    
    // Only update if the signature has actually changed
    if (lastExistingFilesRef.current === existingFilesSignature) {
      //console.log("🟢 [FileUpload] existingFiles haven't actually changed, skipping update");
      return;
    }

    //console.log("🟢 [FileUpload] useEffect - existingFiles changed:", existingFiles.length);
    //console.log("🟢 [FileUpload] Previous signature:", lastExistingFilesRef.current);
    //console.log("🟢 [FileUpload] Current signature:", existingFilesSignature);

    // Update the reference
    lastExistingFilesRef.current = existingFilesSignature;

    // Filter out any attachments with blob URLs and remove duplicates
    const uniqueExistingFiles = existingFiles.filter((attachment, index, arr) => {
      const isBlob = attachment.url.startsWith('blob:');
      if (isBlob) {
        console.warn('🟡 [FileUpload] Skipping blob URL attachment:', attachment.id);
        return false;
      }

      // Check for duplicates based on URL
      const isDuplicate = arr.findIndex(a => a.url === attachment.url) !== index;
      if (isDuplicate) {
        console.warn('🟡 [FileUpload] Removing duplicate attachment:', attachment.id);
        return false;
      }

      return true;
    });

    //console.log("🟢 [FileUpload] uniqueExistingFiles after filtering:", uniqueExistingFiles.length);

    const existingWrappers = uniqueExistingFiles.map(attachment => ({
      id: attachment.id || `existing-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: getFileType(attachment.url),
      name: attachment.url.split('/').pop()?.split('?')[0] || 'Unknown file',
      size: 0,
      isExisting: true,
      url: attachment.url,
      caption: attachment.caption || ''
    }));

    //console.log("🟢 [FileUpload] Created existingWrappers:", existingWrappers.length);

    // ONLY set existing files, don't merge with new files
    // New files will be managed separately through onDrop
    setFiles(existingWrappers);

    // Clear newFiles when existing files are set from upload completion
    if (existingWrappers.length > 0 && newFiles.length > 0) {
      //console.log('🟢 [FileUpload] Clearing newFiles as existingFiles are being set from upload');
      setNewFiles([]);
      onFilesChange([]);
    }

    setLocalChanges({
      newFiles: newFiles, // Keep current newFiles
      deletedUrls: [],
      attachmentOrder: uniqueExistingFiles,
      captions: Object.fromEntries(
        uniqueExistingFiles.map(att => [att.id || '', att.caption || ''])
      )
    });
  }, [existingFiles]); // Only depend on existingFiles

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'],
      'video/*': ['.mp4', '.webm', '.mov'],
      'application/pdf': ['.pdf']
    },
    multiple: true
  });

  return (
    <div className="attachment-manager">
      {/* Debug Panel - Remove in production */}
      {process.env.REACT_APP_ENVIRONMENT === 'DEV' && (
        <div style={{
          position: 'fixed',
          top: '60px',
          right: '10px',
          background: 'rgba(0,0,0,0.9)',
          color: 'white',
          padding: '10px',
          borderRadius: '5px',
          fontSize: '11px',
          zIndex: 10000,
          maxWidth: '300px',
          maxHeight: '400px',
          overflow: 'auto'
        }}>
          <div><strong>FileUpload Debug</strong></div>
          <div>Total files in state: {files.length}</div>
          <div>Existing files: {files.filter(f => f.isExisting).length}</div>
          <div>New files: {files.filter(f => !f.isExisting).length}</div>
          <div>newFiles array: {newFiles.length}</div>
          <div>existingFiles prop: {existingFiles.length}</div>
          <div>isCompressing: {isCompressing ? 'true' : 'false'}</div>
          <div>processingFiles size: {processingFiles.current.size}</div>
          <div>lastSignature: {lastExistingFilesRef.current.substring(0, 50)}...</div>
        </div>
      )}

      <LoadingOverlay
        isVisible={isCompressing}
        size="medium"
        text="Optimizing Images"
        subtext={compressionProgress || "Compressing images to reduce file size..."}
        debug={false}
      />

      <div {...getRootProps({ className: 'dropzone' })}>
        <input {...getInputProps()} />
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontSize: '18px', fontWeight: '500', marginBottom: '8px' }}>
            Drag and drop files here, or click to select files
          </p>
          <p style={{ fontSize: '14px', color: '#666', fontWeight: '400' }}>
            Images will be automatically optimized for faster loading
          </p>
        </div>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={files.map(f => f.id)} strategy={rectSortingStrategy}>
          <div className="attachment-list">
            {files.map(file => (
              <SortableFile
                key={file.id}
                fileWrapper={file}
                onCaptionChange={handleCaptionSave}
                onDelete={handleDelete}
                jobId={jobId}
                onSave={handleCaptionSave}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default FileUpload;